import { DownOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Alert, Button, Checkbox, Collapse, Space, Spin, Tooltip, Typography } from 'antd';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { t } from 'ttag';

import {
  EVENT_DETAILS_TOOLTIP,
  REQUIRES_BY_PATH,
  UNAVAILABLE_TOOLTIP,
  buildModules,
  defaultSelection,
  labelByPath,
} from './modules';
import { computeLeafStates, descendantPaths, groupState } from './selection';

const ModuleBox = styled(Collapse)`
  &&& .ant-collapse-header {
    align-items: center;
    padding: 10px 16px;
  }
  &&& .ant-collapse-content-box {
    padding: 12px 16px 16px 44px;
  }
  /* antd pads both sides of the checkbox label; the row's flex gap is the spacing. */
  &&& .ant-checkbox + span {
    padding-inline-end: 0;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const RightSlot = styled.div`
  margin-left: auto;
`;

const ChildGrid = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: repeat(${({ $rows }) => $rows}, auto);
  column-gap: 48px;
  row-gap: 12px;
`;

const ChildRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Hint = styled(Typography.Text)`
  font-size: 0.85em;
`;

const InfoIcon = ({ title }) => (
  <Tooltip title={title}>
    <Typography.Text type="secondary">
      <InfoCircleOutlined />
    </Typography.Text>
  </Tooltip>
);

InfoIcon.propTypes = { title: PropTypes.string.isRequired };

const disabledReason = (path, states, labels) => {
  const state = states[path];
  if (!state || state.enabled) return null;
  if (!state.available) return UNAVAILABLE_TOOLTIP();
  const requires = REQUIRES_BY_PATH[path];
  if (requires && !states[requires]?.checked) {
    return t`Requires ${labels[requires]} to be selected.`;
  }
  return null;
};

const leafStateShape = PropTypes.shape({
  available: PropTypes.bool,
  enabled: PropTypes.bool,
  checked: PropTypes.bool,
});

const ChildLeaf = ({ leaf, states, labels, onToggle }) => {
  const state = states[leaf.path] || {};
  const tooltip = disabledReason(leaf.path, states, labels) || leaf.info;
  return (
    <ChildRow>
      {leaf.alwaysOn ? (
        <Checkbox checked disabled>
          {leaf.label}
        </Checkbox>
      ) : (
        <Checkbox
          checked={state.checked}
          disabled={!state.enabled}
          onChange={e => onToggle(leaf.path, e.target.checked)}
        >
          {leaf.label}
        </Checkbox>
      )}
      {tooltip && <InfoIcon title={tooltip} />}
    </ChildRow>
  );
};

ChildLeaf.propTypes = {
  leaf: PropTypes.shape({
    path: PropTypes.string,
    label: PropTypes.string.isRequired,
    info: PropTypes.string,
    alwaysOn: PropTypes.bool,
  }).isRequired,
  states: PropTypes.objectOf(leafStateShape).isRequired,
  labels: PropTypes.objectOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
};

// Parent rows with their own payload key (Registration, Event App) toggle the
// module's `enabled` flag; pure groups (Libraries, MobiAI) aggregate children.
const parentCheckboxState = (module, states) => {
  if (!module.path) {
    return groupState(module, states);
  }
  const state = states[module.path] || {};
  const availableChildren = (module.children || []).filter(c => states[c.path]?.enabled);
  const someUnchecked = availableChildren.some(c => !states[c.path]?.checked);
  return {
    checked: state.checked,
    disabled: !state.available,
    indeterminate: state.checked && availableChildren.length > 0 && someUnchecked,
  };
};

const ModulePanel = ({ module, states, labels, onToggle, onToggleMany }) => {
  const [open, setOpen] = useState(false);
  const hasChildren = !!module.children;
  const parent = parentCheckboxState(module, states);
  const reason = module.path ? disabledReason(module.path, states, labels) : null;

  const onParentChange = checked => {
    if (module.path) {
      onToggle(module.path, checked);
    } else {
      onToggleMany(descendantPaths(module).reduce((acc, p) => ({ ...acc, [p]: checked }), {}));
    }
  };

  const header = (
    <HeaderRow>
      <Checkbox
        checked={parent.checked}
        indeterminate={parent.indeterminate}
        disabled={parent.disabled}
        onChange={e => onParentChange(e.target.checked)}
      >
        {module.label}
      </Checkbox>
      {module.hint && <Hint type="secondary">{module.hint}</Hint>}
      {reason && <InfoIcon title={reason} />}
      {hasChildren && (
        <RightSlot>
          <Button
            type="link"
            size="small"
            style={{ paddingInline: 0 }}
            onClick={() => setOpen(v => !v)}
          >
            {t`More options`}
            <DownOutlined rotate={open ? 180 : 0} />
          </Button>
        </RightSlot>
      )}
    </HeaderRow>
  );

  const rows = hasChildren ? Math.ceil(module.children.length / 2) : 0;

  return (
    <ModuleBox
      activeKey={open ? [module.id] : []}
      expandIcon={() => null}
      items={[
        {
          key: module.id,
          label: header,
          collapsible: 'icon',
          showArrow: false,
          children: hasChildren ? (
            <ChildGrid $rows={rows}>
              {module.children.map(child => (
                <ChildLeaf
                  key={child.id}
                  leaf={child}
                  states={states}
                  labels={labels}
                  onToggle={onToggle}
                />
              ))}
            </ChildGrid>
          ) : null,
        },
      ]}
    />
  );
};

ModulePanel.propTypes = {
  module: PropTypes.shape({
    id: PropTypes.string.isRequired,
    path: PropTypes.string,
    label: PropTypes.string.isRequired,
    hint: PropTypes.string,
    children: PropTypes.arrayOf(
      PropTypes.shape({ id: PropTypes.string, path: PropTypes.string, label: PropTypes.string }),
    ),
  }).isRequired,
  states: PropTypes.objectOf(leafStateShape).isRequired,
  labels: PropTypes.objectOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  onToggleMany: PropTypes.func.isRequired,
};

const DuplicationChecklist = ({
  selection = null,
  onSelectionChange,
  isConfigEnabled,
  isLoading = false,
  hasError = false,
}) => {
  const modules = useMemo(() => buildModules(), []);
  const labels = useMemo(() => labelByPath(modules), [modules]);
  const current = useMemo(() => selection || defaultSelection(modules), [selection, modules]);
  const states = useMemo(
    () => computeLeafStates(modules, current, isConfigEnabled),
    [modules, current, isConfigEnabled],
  );

  const onToggle = (path, value) => onSelectionChange({ ...current, [path]: value });
  const onToggleMany = changes => onSelectionChange({ ...current, ...changes });

  // Availability comes from the source event's config, so there is nothing
  // truthful to show until it arrives.
  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', margin: '1em' }}>
        <Spin size="small" />
      </div>
    );
  }

  if (hasError) {
    return (
      <Alert
        type="error"
        showIcon
        title={t`We couldn't load this event's content. Please try again.`}
      />
    );
  }

  return (
    <Space orientation="vertical" size={8} style={{ width: '100%' }}>
      <Typography.Text strong>{t`Select what to duplicate`}</Typography.Text>
      <ModuleBox
        collapsible="disabled"
        activeKey={[]}
        expandIcon={() => null}
        items={[
          {
            key: 'event_details',
            showArrow: false,
            label: (
              <HeaderRow>
                <Checkbox checked disabled>
                  {t`Event Details`}
                </Checkbox>
                <RightSlot>
                  <InfoIcon title={EVENT_DETAILS_TOOLTIP()} />
                </RightSlot>
              </HeaderRow>
            ),
          },
        ]}
      />
      {modules.map(module => (
        <ModulePanel
          key={module.id}
          module={module}
          states={states}
          labels={labels}
          onToggle={onToggle}
          onToggleMany={onToggleMany}
        />
      ))}
    </Space>
  );
};

DuplicationChecklist.propTypes = {
  selection: PropTypes.objectOf(PropTypes.bool),
  onSelectionChange: PropTypes.func.isRequired,
  isConfigEnabled: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  hasError: PropTypes.bool,
};

export default DuplicationChecklist;
